/**
 * A project's console variables as they are stored (§5.11): the bounds, the key
 * grammar, and the sanitizer the editor's Save goes through. Substitution — what
 * `{{name}}` means in a request — stays in the desktop app; what a variable *is*
 * is shared, because a project an organization shares carries them (§5.25).
 */
import type { ApiEnvironmentInput, ApiVariableInput } from './types';

/** How many of each a project may keep — a bound on the editor, not a quota. */
export const MAX_VARIABLES = 200;
export const MAX_ENVIRONMENTS = 20;
export const MAX_KEY_CHARS = 60;
export const MAX_ENVIRONMENT_NAME_CHARS = 40;
/** A value is a host, a token, an id — not a document. */
export const MAX_VALUE_CHARS = 4096;

/** What may be written between the braces and read back as the same name. */
const KEY = /^[A-Za-z0-9_.-]+$/;

/**
 * A key as the editor stores it. The braces are stripped because typing
 * `{{token}}` into a field labelled with braces is what everyone does first,
 * and it is a name, not a mistake.
 */
export function normalizeKey(raw: string): string {
  return raw.trim().replace(/^\{+/, '').replace(/\}+$/, '').trim().slice(0, MAX_KEY_CHARS);
}

export function isVariableKey(key: string): boolean {
  return key.length > 0 && key.length <= MAX_KEY_CHARS && KEY.test(key);
}

/**
 * A whole set as it will be stored (§5.11) — what the editor sends, held to what
 * a request can actually use. It throws the sentence the user gets back, so the
 * rules are one thing rather than one per surface: the editor's Save button is
 * disabled by the same reading main refuses on.
 *
 * A row with no key yet is dropped rather than refused — an empty line under
 * the last variable is how a form invites the next one — and a value written for
 * an environment the same form deleted goes with it: an id nothing can select
 * again is not a value, it is a leak.
 */
export function sanitizeVariables(input: { environments: ApiEnvironmentInput[]; variables: ApiVariableInput[] }): {
  environments: ApiEnvironmentInput[];
  variables: ApiVariableInput[];
} {
  if (input.environments.length > MAX_ENVIRONMENTS)
    throw new Error(`A project keeps at most ${MAX_ENVIRONMENTS} environments.`);
  if (input.variables.length > MAX_VARIABLES) throw new Error(`A project keeps at most ${MAX_VARIABLES} variables.`);

  const environments: ApiEnvironmentInput[] = [];
  for (const environment of input.environments) {
    const name = environment.name.trim().slice(0, MAX_ENVIRONMENT_NAME_CHARS);
    if (!name) throw new Error('Give every environment a name.');
    environments.push({ id: rowId(environment.id, environments), name });
  }

  const known = new Set(environments.map((e) => e.id));
  const variables: ApiVariableInput[] = [];
  for (const variable of input.variables) {
    const key = normalizeKey(variable.key);
    if (!key) continue;
    if (!isVariableKey(key)) {
      throw new Error(`“${key}” cannot be written as a variable — use letters, digits, dot, dash or underscore.`);
    }
    if (variables.some((v) => v.key === key)) throw new Error(`Two variables share the key “${key}”.`);
    const values: Record<string, string> = {};
    for (const [environmentId, value] of Object.entries(variable.values)) {
      if (known.has(environmentId)) values[environmentId] = value.slice(0, MAX_VALUE_CHARS);
    }
    variables.push({
      id: rowId(variable.id, variables),
      key,
      value: variable.value.slice(0, MAX_VALUE_CHARS),
      values,
      isSecret: variable.isSecret === true
    });
  }
  return { environments, variables };
}

/**
 * An id the editor minted. It is opaque — the editor owns its rows between two
 * Saves, which is what lets a value name an environment SQLite has never seen —
 * but it still has to look like an id, and be the only row carrying it, or one
 * row would silently overwrite another.
 */
function rowId(raw: string, taken: { id: string }[]): string {
  if (!/^[A-Za-z0-9_-]{1,40}$/.test(raw)) throw new Error('That editor sent a row without a usable id.');
  if (taken.some((row) => row.id === raw)) throw new Error('That editor sent two rows with the same id.');
  return raw;
}
